import { Component, inject, signal, computed, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import {
  SidebarComponent,
  SidebarItem,
  ThemeService,
  AvatarComponent,
  DividerComponent,
  ToastContainerComponent,
  DropdownMenuComponent,
  DropdownMenuItem,
  BannerComponent,
  ButtonComponent,
} from '@ams/ui-kit';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { AuthService } from '../../services/auth.service';
import { AccessControlService } from '../../services/access-control.service';
import { TenantSettingsService } from '../../services/tenant-settings.service';
import { IconService } from '../../services/icon.service';
import { PortalService } from '../../services/portal.service';
import { PlanEntitlementService } from '../../services/plan-entitlement.service';
import { INVOICE_PAYMENT_BANNER_KEY, InvoicePaymentService } from '../../services/invoice-payment.service';
import { PortalContentType } from '../../models/portal/portal.model';
import { FooterComponent } from '../shell/footer/footer.component';
import { GlobalSearchComponent } from '../../modules/search/global-search.component';
import { PendingMembershipBannerComponent } from '../pending-membership-banner/pending-membership-banner.component';
// ChatbotPanelComponent is imported so the shared shell template's @if branch
// type-checks. portalMode() is always true in this component, so the panel
// is never actually rendered here — admin-only feature for v1.
import { ChatbotPanelComponent } from '../chatbot-panel/chatbot-panel.component';
import { NotificationBellComponent } from '../shell/notification-bell/notification-bell.component';

/**
 * Portal Shell Component
 * Reuses shell template/styles, dedicated to member portal routes
 */
@Component({
  selector: 'app-portal-shell',
  standalone: true,
  imports: [CommonModule, RouterModule, SidebarComponent, AvatarComponent, DividerComponent, ToastContainerComponent, FontAwesomeModule, FooterComponent, DropdownMenuComponent, GlobalSearchComponent, PendingMembershipBannerComponent, ChatbotPanelComponent, NotificationBellComponent, BannerComponent, ButtonComponent],
  templateUrl: '../shell/shell.component.html',  // Reuse existing template
  styleUrl: '../shell/shell.component.scss',      // Reuse existing styles
})
export class PortalShellComponent implements OnInit {
  readonly themeService = inject(ThemeService);
  readonly authService = inject(AuthService);
  readonly accessControl = inject(AccessControlService);
  readonly tenantSettings = inject(TenantSettingsService);
  readonly iconService = inject(IconService);
  readonly portalService = inject(PortalService);
  // Required because this component reuses shell.component.html, which renders the
  // billing-degraded banner. Anything the shared template touches must be bound on
  // BOTH shells or the build fails here rather than in ShellComponent.
  readonly planEntitlements = inject(PlanEntitlementService);
  // Same reason: the template's "Pay now" button binds these. It never renders in
  // portal mode (the notice is gated on !portalMode()), but AOT type-checks the whole
  // template against this component regardless.
  readonly invoicePayment = inject(InvoicePaymentService);
  readonly invoicePaymentBannerKey = INVOICE_PAYMENT_BANNER_KEY;
  private readonly router = inject(Router);

  readonly sidebarCollapsed = signal<boolean>(false);
  readonly copilotOpen = signal<boolean>(false);
  readonly mobileSidebarOpen = signal<boolean>(false);
  readonly userMenuOpen = signal<boolean>(false);
  readonly branding = this.tenantSettings.branding;

  // Always true for portal shell
  readonly portalMode = signal<boolean>(true);

  // Not used in portal but template references it
  readonly isMemboraAdmin = computed(() => false);

  /**
   * Required by the shared shell.component.html (the degraded-plan banner), NOT
   * by anything this component renders itself. That template is reused verbatim
   * by ShellComponent and this one, so every member it binds must exist on both
   * or the AOT build fails — which is exactly what the banner did when it landed.
   */
  readonly isPlatformTenant = computed(
    () => !!this.authService.currentUser()?.isPlatformTenant,
  );

  readonly currentUserName = computed(() => {
    const user = this.authService.currentUser();
    if (!user) return 'User';
    return `${user.firstName} ${user.lastName}`.trim() || user.email || 'User';
  });

  readonly logoUrl = computed(() => {
    const b = this.tenantSettings.branding();
    return b?.logoUrl || '/assets/logo-light.svg';
  });

  readonly logoAlt = computed(() => this.tenantSettings.generalSettings()?.orgName || 'Portal');

  readonly orgName = computed(() => {
    const me = this.portalService.portalMe();
    return me?.organization?.name ?? 'My Organization';
  });

  readonly userRoleLabel = computed(() => {
    const user = this.authService.currentUser();
    if (!user?.orgRoleAssignments?.length) return 'Member';

    const allRoleNames = new Set<string>();
    user.orgRoleAssignments.forEach(a => {
      a.roleNames?.forEach(name => allRoleNames.add(name));
    });

    if (allRoleNames.has('Member Admin')) return 'Member Admin';
    if (allRoleNames.has('Member User')) return 'Member User';
    return 'Member';
  });

  private readonly BUILTIN_MODEL_KEYS = ['announcement', 'news'];
  readonly extraContentTypes = signal<PortalContentType[]>([]);

  readonly filteredNavItems = computed(() => {
    const access = this.accessControl.effectiveAccess();
    const baseItems = [...this.portalNavigationItems];

    // Insert Extra content types before the admin divider
    const extras = this.extraContentTypes();
    if (extras.length > 0) {
      const adminDividerIndex = baseItems.findIndex(i => i.id === 'portal-divider-admin');
      const extraItems: SidebarItem[] = [
        { id: 'portal-divider-extra', label: '', divider: true },
        { id: 'portal-section-extra', label: '', section: 'Extra' },
        ...extras.map(ct => ({
          id: `portal-content-${ct.key}`,
          label: ct.name,
          route: `/portal/content/${ct.key}`,
          icon: this.iconService.getIcon('document'),
        })),
      ];
      if (adminDividerIndex >= 0) {
        baseItems.splice(adminDividerIndex, 0, ...extraItems);
      } else {
        baseItems.push(...extraItems);
      }
    }

    return this.filterNavItems(baseItems, access);
  });

  private filterNavItems(items: SidebarItem[], _access: Map<string, string>): SidebarItem[] {
    return items
      .filter(item => {
        if (item.hidden) return false;
        if (item.divider || item.section) return true;
        return this.accessControl.canViewMenuItem(item.id);
      })
      .map(item => {
        if (item.children) {
          const filteredChildren = item.children.filter(child =>
            !child.hidden && this.accessControl.canViewMenuItem(child.id)
          );
          if (filteredChildren.length === 0) return null;
          return { ...item, children: filteredChildren };
        }
        return item;
      })
      .filter((item): item is SidebarItem => item !== null)
      .filter((item, index, arr) => {
        if (item.divider || item.section) {
          const nextItem = arr[index + 1];
          return nextItem && !nextItem.divider && !nextItem.section;
        }
        return true;
      });
  }

  readonly portalNavigationItems: SidebarItem[] = [
    { id: 'portal-dashboard', label: 'Dashboard', route: '/portal/dashboard', icon: this.iconService.getIcon('dashboard') },
    { id: 'portal-events', label: 'Events', route: '/portal/events', icon: this.iconService.getIcon('events') },
    { id: 'portal-announcements', label: 'Announcements', route: '/portal/announcements', icon: this.iconService.getIcon('notifications') },
    { id: 'portal-news', label: 'News', route: '/portal/news', icon: this.iconService.getIcon('document') },
    { id: 'portal-divider-admin', label: '', divider: true },
    { id: 'portal-section-admin', label: '', section: 'Administration' },
    { id: 'portal-account', label: 'Account Setup', route: '/portal/account', icon: this.iconService.getIcon('settings') },
    { id: 'portal-users', label: 'Users', route: '/portal/users', icon: this.iconService.getIcon('staff') },
    {
      id: 'portal-billing', label: 'Billing', icon: this.iconService.getIcon('billing'),
      children: [
        { id: 'portal-invoices', label: 'Invoices', route: '/portal/billing/invoices' },
        { id: 'portal-payment-methods', label: 'Payment Methods', route: '/portal/billing/payment-methods' },
      ],
    },
  ];

  readonly activeOrgName = computed(() => this.authService.activeOrg()?.orgName ?? null);

  // Sidebar brand: association name always shown, with the member's own
  // organization as a badge underneath (mirrors the standalone member portal).
  readonly associationName = computed(() => this.tenantSettings.generalSettings()?.orgName || 'Membora AMS');
  readonly chapterBadge = computed(() => this.portalService.portalMe()?.organization?.name ?? '');

  // Org switcher — portal users typically have a single org, hide switcher
  readonly showOrgSwitcher = computed(() => false);

  readonly userMenuItems = computed<DropdownMenuItem[]>(() => [
    { label: 'Account', action: 'profile', section: 'Account', sectionIcon: this.iconService.getIcon('user-menu') },
    { label: 'Log out', action: 'logout', variant: 'destructive', icon: this.iconService.getIcon('logout'), dividerBefore: true },
  ]);

  ngOnInit(): void {
    this.portalService.getMe().subscribe();
    this.portalService.getContentTypes().subscribe({
      next: (types) => {
        this.extraContentTypes.set(
          types.filter(t => !this.BUILTIN_MODEL_KEYS.includes(t.key))
        );
      },
    });
  }

  switchOrg(_orgId: string | null): void { /* not used in portal */ }

  onUserMenuAction({ action }: { action: string }): void {
    switch (action) {
      case 'profile': this.onProfileClick(); break;
      case 'logout': this.logout(); break;
    }
  }

  onSidebarCollapse(collapsed: boolean): void { this.sidebarCollapsed.set(collapsed); }
  toggleTheme(): void { this.themeService.toggleMode(); }
  toggleCopilot(): void { /* not used in portal */ }
  toggleChatbot(): void { /* not used in portal — admin shell only */ }
  toggleUserMenu(): void { this.userMenuOpen.update(v => !v); }
  closeUserMenu(): void { this.userMenuOpen.set(false); }
  onProfileClick(): void { this.closeUserMenu(); this.router.navigate(['/portal/account']); }
  toggleMobileSidebar(): void { this.mobileSidebarOpen.update(v => !v); }
  closeMobileSidebar(): void { this.mobileSidebarOpen.set(false); }
  openSearch(): void { /* not used in portal */ }
  switchPersona(): void { this.router.navigate(['/launcher']); }
  logout(): void { this.authService.logout(); }
}
